/**
 * Echo Hunt -- DB/store wrappers for the user's `EchoProfile` (P3).
 *
 * Per the ground rules (§16.0), pure logic lives in sibling modules with no `store`/DB imports; this
 * thin `*_Live.ts` wrapper is where the profile row is read/written.
 */
import {MeID} from "../Server/DB/users.js";
import {EchoProfile, GetEchoProfiles} from "../Server/DB/echoProfiles/@EchoProfile.js";
import {AddEchoProfile, UpdateEchoProfile} from "../Server/Commands/EchoProfileCommands.js";
import {GetAsync} from "mobx-graphlink";
import {ClicheBlacklistEntry, ClicheBlacklistEntrySource, EchoDifficultyState, EchoSuspicionSettings} from "./Types.js";
import {
	AddClicheEntry,
	BlacklistEditResult,
	RemoveClicheEntries,
	RemoveClicheEntry,
	RenameClicheEntry,
} from "./Blacklist.js";
import {LLMRole, NormalizeModelOverride, ReasoningEffort, RoleModelMap, RoleReasoningMap} from "./Roles.js";
import {DefaultEchoJudgeSettings} from "./Judge.js";
import {NormalizeSuspicionSettings} from "./Story.js";
import {EchoJudgeSettings} from "./Types.js";

/** Default X (share of real bullets) and Y (min miscategorization), per §16.1. */
export const DEFAULT_X_SHARE = .65;
export const DEFAULT_Y_TARGET = .5;

export function DefaultDifficultyState(): EchoDifficultyState {
	return {
		yTarget: DEFAULT_Y_TARGET,
		responseDeadlineMS: null,
		rewriteStrength: "medium",
		fakeTypicalityThreshold: .9,
		adjusting: null,
	};
}

/** The signed-in user's profile (the first one, if they made several). Null when signed out or when
 * no profile has been created yet. Reactive: this is a mobx-graphlink accessor.
 *
 * SYNC. Reading it before its data has arrived throws a `BailError`, which only a React `observer`
 * catches -- so an async path (a click handler, a `useEffect`) must wrap it: `await GetAsync(()=>
 * GetMyEchoProfile())`. There is deliberately no async variant; the wrap is the whole fix. */
export function GetMyEchoProfile(): EchoProfile|n {
	const userID = MeID();
	if (userID == null) return null;
	return GetEchoProfiles(userID)[0] ?? null;
}

let createInFlight: Promise<string>|n = null;

/** Returns the signed-in user's profile, creating it if this is their first Echo Hunt visit.
 * Rejects when not signed in. */
export async function EnsureEchoProfile_Live(): Promise<EchoProfile> {
	const existing = GetMyEchoProfile();
	if (existing != null) return existing;

	const userID = MeID();
	if (userID == null) throw new Error("You must be signed in to use Echo Hunt.");

	// guard against double-creation from a double click / two panels mounting at once
	createInFlight ??= new AddEchoProfile({entry: new EchoProfile({
		xShare: DEFAULT_X_SHARE,
		yTarget: DEFAULT_Y_TARGET,
		difficulty: DefaultDifficultyState(),
		bannedTerms: [],
		schematicRules: [],
		worldPool: [],
		clicheBlacklist: [],
		modelRoles: {},
		judge: DefaultEchoJudgeSettings(),
	})}).RunOnServer().then(a=>a.id).finally(()=>{
		createInFlight = null;
	});
	const id = await createInFlight;

	// the row exists server-side now; read it back through the accessor (which the caller's observer
	// re-renders against once the subscription delivers)
	const profile = GetEchoProfiles(userID).find(a=>a.id == id) ?? null;
	return profile ?? new EchoProfile({id, creator: userID, createdAt: Date.now()});
}

/** Sets (or clears, with an empty string) one role's model override. */
export async function SetModelRole_Live(profileID: string, role: LLMRole, model: string|n): Promise<void> {
	const profile = GetEchoProfiles(MeID() ?? null).find(a=>a.id == profileID) ?? null;
	const modelRoles: RoleModelMap = {...(profile?.modelRoles ?? {})};

	const normalized = NormalizeModelOverride(model);
	if (normalized == null) {
		delete modelRoles[role];
	} else {
		modelRoles[role] = normalized;
	}

	await new UpdateEchoProfile({id: profileID, updates: {modelRoles}}).RunOnServer();
}

/** Sets (or clears, with null) one role's reasoning-effort override (2026-10-21). Clearing means "auto". */
export async function SetModelReasoning_Live(profileID: string, role: LLMRole, effort: ReasoningEffort|n): Promise<void> {
	const profile = GetEchoProfiles(MeID() ?? null).find(a=>a.id == profileID) ?? null;
	const modelReasoning: RoleReasoningMap = {...(profile?.modelReasoning ?? {})};

	if (effort == null) {
		delete modelReasoning[role];
	} else {
		modelReasoning[role] = effort;
	}

	await new UpdateEchoProfile({id: profileID, updates: {modelReasoning}}).RunOnServer();
}

/** One role's reasoning-effort override, or null for "auto". */
export function GetModelReasoning(role: LLMRole): ReasoningEffort|n {
	return GetMyEchoProfile()?.modelReasoning?.[role] ?? null;
}

// weaver paraphrase note (2026-10-21)
// ==========

/** Whether the weaver is told it may reword the bullets entirely, not just bridge between them. Off by
 * default: the paraphrase is the reshape step's job, and loosening the weaver is an experiment (there is no
 * retention check on woven text, so a heavier rewrite risks eroding an event unnoticed). */
export function GetWeaveParaphraseNote(): boolean {
	return GetMyEchoProfile()?.weaveParaphraseNote == true;
}

export async function SetWeaveParaphraseNote_Live(enabled: boolean): Promise<boolean> {
	const profile = await GetAsync(()=>GetMyEchoProfile());
	if (profile == null) return false;
	await new UpdateEchoProfile({id: profile.id, updates: {weaveParaphraseNote: enabled}}).RunOnServer();
	return true;
}

// judge loop settings (§9.1-§9.3, §5.5)
// ==========

/** The profile's judge settings, defaulted for a profile created before P10 existed (§16.1's `.4`-`.6`
 * band, 3 repair attempts, 2 rewrites per bullet, rejection sampling off).
 *
 * Lives here rather than in `Judge_Live.ts` so that `Reshape_Live` can read the rejection-sampling toggle
 * without importing the judge LOOP -- which imports `Reshape_Live` back for its resample callbacks. */
export function GetJudgeSettings(): EchoJudgeSettings {
	return GetMyEchoProfile()?.judge ?? DefaultEchoJudgeSettings();
}

/** Writes the judge settings (§9.2, §9.3) to the profile, merging over the current values. */
export async function SetJudgeSettings_Live(updates: Partial<EchoJudgeSettings>): Promise<boolean> {
	const profile = await GetAsync(()=>GetMyEchoProfile());
	if (profile == null) return false;
	await new UpdateEchoProfile({id: profile.id, updates: {judge: {...GetJudgeSettings(), ...updates}}}).RunOnServer();
	return true;
}

// suspicion meter settings (§8.4, P11)
// ==========

/** The profile's suspicion settings, NORMALIZED -- so a `NaN` from an empty input box, a threshold of 0
 * from a slider dragged to the end, or an absent field on a pre-P11 profile all resolve to a meter that
 * actually works. Callers never see the raw stored value on purpose: every consumer (the meter, the
 * settings panel, the reveal's wording) must agree about the effective settings or they disagree about
 * whether a story has ended. */
export function GetSuspicionSettings(): EchoSuspicionSettings {
	return NormalizeSuspicionSettings(GetMyEchoProfile()?.suspicion);
}

/** Writes the suspicion settings, merging over the current (normalized) values. */
export async function SetSuspicionSettings_Live(updates: Partial<EchoSuspicionSettings>): Promise<boolean> {
	const profile = await GetAsync(()=>GetMyEchoProfile());
	if (profile == null) return false;
	const merged = NormalizeSuspicionSettings({...GetSuspicionSettings(), ...updates});
	await new UpdateEchoProfile({id: profile.id, updates: {suspicion: merged}}).RunOnServer();
	return true;
}

// cliché blacklist (§5.5, §13.3)
// ==========
/** Writes the whole cliché-blacklist list. The pure edit helpers in `Blacklist.ts` produce the list; this
 * only persists it, so the add/rename/remove semantics are unit-testable without a DB. */
export async function SetClicheBlacklist_Live(entries: ClicheBlacklistEntry[]): Promise<boolean> {
	const profile = await GetAsync(()=>GetMyEchoProfile());
	if (profile == null) return false;
	await new UpdateEchoProfile({id: profile.id, updates: {clicheBlacklist: entries}}).RunOnServer();
	return true;
}

/** Adds one phrase to the blacklist, reporting why when it cannot be added (§5.5). */
export async function AddClicheEntry_Live(phrase: string, source: ClicheBlacklistEntrySource = "manual"): Promise<BlacklistEditResult> {
	const profile = await GetAsync(()=>GetMyEchoProfile());
	if (profile == null) return {ok: false, error: "No Echo Hunt profile yet."};

	const result = AddClicheEntry(profile.clicheBlacklist ?? [], phrase, source);
	if (!result.ok) return result;

	await new UpdateEchoProfile({id: profile.id, updates: {clicheBlacklist: result.entries}}).RunOnServer();
	return result;
}

/** Renames one entry, preserving its source and hit count (§13.3). */
export async function RenameClicheEntry_Live(oldPhrase: string, newPhrase: string): Promise<BlacklistEditResult> {
	const profile = await GetAsync(()=>GetMyEchoProfile());
	if (profile == null) return {ok: false, error: "No Echo Hunt profile yet."};

	const result = RenameClicheEntry(profile.clicheBlacklist ?? [], oldPhrase, newPhrase);
	if (!result.ok) return result;

	await new UpdateEchoProfile({id: profile.id, updates: {clicheBlacklist: result.entries}}).RunOnServer();
	return result;
}

/** Removes one entry by phrase. */
export async function RemoveClicheEntry_Live(phrase: string): Promise<boolean> {
	const profile = await GetAsync(()=>GetMyEchoProfile());
	if (profile == null) return false;
	await new UpdateEchoProfile({id: profile.id, updates: {clicheBlacklist: RemoveClicheEntry(profile.clicheBlacklist ?? [], phrase)}}).RunOnServer();
	return true;
}

/** Removes several entries at once. */
export async function RemoveClicheEntries_Live(phrases: string[]): Promise<boolean> {
	const profile = await GetAsync(()=>GetMyEchoProfile());
	if (profile == null) return false;
	await new UpdateEchoProfile({id: profile.id, updates: {clicheBlacklist: RemoveClicheEntries(profile.clicheBlacklist ?? [], phrases)}}).RunOnServer();
	return true;
}
